import React, { useMemo, useState } from "react";
import * as yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { Button, Form, ButtonToolbar } from "react-bootstrap";
import { UserGroupChild } from "../../../api/usergroup";

export interface RVModelFormValues {
  name: string;
  desc: string;
  // the user group the model belongs to; only chosen by a model/order admin
  groupID?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const schema = (requireGroup: boolean) =>
  yup.object<RVModelFormValues>().shape({
    name: yup.string().label("Name").trim().min(3).max(100).required(),
    desc: yup.string().label("Description").trim().min(0).max(300).required(),
    groupID: requireGroup
      ? yup.string().label("Group").required()
      : yup.string().label("Group").notRequired(),
  });

const DEFAULT_VALUES: RVModelFormValues = {
  name: "",
  desc: "",
  groupID: "",
};

interface RVModelDetailsProps {
  defaultValues?: RVModelFormValues;
  onSubmit: (v: RVModelFormValues) => Promise<void> | void;
  onCancel?: () => void;
  confirmButtonLabel?: string;
  // the user groups the model can be created in. When given, one must be
  // chosen; otherwise the model goes in the user's own group.
  groups?: UserGroupChild[];
}

const RVModelDetails = (props: RVModelDetailsProps): JSX.Element => {
  const {
    confirmButtonLabel = "Create",
    defaultValues = DEFAULT_VALUES,
    onSubmit,
    onCancel,
    groups,
  } = props;

  const [values, setValues] = useState(defaultValues);
  const pickGroup = undefined !== groups;
  const validationSchema = useMemo(() => schema(pickGroup), [pickGroup]);

  const handleFormSubmit = async (
    values: RVModelFormValues,
    { setSubmitting, resetForm }: FormikHelpers<RVModelFormValues>
  ): Promise<void> => {
    try {
      await onSubmit(values);
      setValues(defaultValues);
      resetForm();
    } catch (e) {
      console.log(e);
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={values}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
      validateOnMount={true}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        isSubmitting,
        isValid,
        resetForm,
        dirty,
      }): JSX.Element => (
        <Form
          noValidate
          onSubmit={(e: React.FormEvent<HTMLFormElement>): void =>
            handleSubmit(e as React.FormEvent<HTMLFormElement>)
          }
        >
          {pickGroup && (
            <Form.Group>
              <Form.Label>Group</Form.Label>
              <Form.Control
                as="select"
                name="groupID"
                data-testid="model-group"
                value={values.groupID || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={(touched.groupID || isValid) && !errors.groupID}
              >
                <option value="">Choose the manufacturer group</option>
                {(groups || []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                The manufacturer the model belongs to. Its orders are visible to
                the members of this group.
              </Form.Text>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter model name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={(touched.name || isValid) && !errors.name}
            />
            {errors.name && touched.name && (
              <Form.Text>{errors.name}</Form.Text>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter model description"
              name="desc"
              value={values.desc}
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={(touched.desc || isValid) && !errors.desc}
            />
            {errors.desc && touched.desc && (
              <Form.Text>{errors.desc}</Form.Text>
            )}
          </Form.Group>
          <ButtonToolbar>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !isValid || !dirty}
            >
              {confirmButtonLabel}
            </Button>
            {onCancel && (
              <Button
                variant="secondary"
                className="ml-1"
                onClick={(): void => {
                  const r = window.confirm("Are you sure?");
                  if (true === r) {
                    resetForm();
                    onCancel();
                  }
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
          </ButtonToolbar>
        </Form>
      )}
    </Formik>
  );
};

export default RVModelDetails;
